import { formatCamelCaseToSpaceSeparateWords } from 'syf-js-utilities';
import { CHECKING, CREDIT_CARD, PINLESS_DEBIT_CARD } from '../const/paymentMedium';
import { paymentTypes } from '../const/paymentOptions';
import { getPaymentMethodsFromPaymentEnterpriseEligibility } from '../helpers';
import getDCHMessageType from '../helpers/getDCHMessageType';
import en from '../strings/lang/en';
import { AnalyticsAccountStatuses, AnalyticsPaymentMethods } from '../typings/analytics';
import { ELIGIBILITY_TYPE } from '../const/eligibilityType';
import { promoTypes } from '../const/productType';
import { isIntroRateLoanPromoAvailableForAutopay } from '../helpers/parseEligibility';
import { isIntroRateLoanEnrolled, isPayLaterProduct, isRevolvingCreditProduct, isSecuredInstallmentLoanProduct, isSetpayLegacyProduct, isWaivedInterestEnrolled } from '../helpers/productTypeHelpers';
import { getValues } from '../xform';
export const getPaymentOptionAndMethod = (form, alternatePaymentMethods, paymentMethods) => {
    const allPaymentMethods = [
        ...paymentMethods.paymentMethods,
        ...alternatePaymentMethods.paymentMethods
    ];
    const paymentForm = getValues(form.getSnapshot().context.actors);
    const paymentMethod = allPaymentMethods === null || allPaymentMethods === void 0 ? void 0 : allPaymentMethods.find((method) => {
        return `${method === null || method === void 0 ? void 0 : method.id}` === (paymentForm === null || paymentForm === void 0 ? void 0 : paymentForm.paymentMethod);
    });
    return [paymentForm.paymentOption, paymentMethod];
};
export const getPaymentFlowFormValues = (context) => {
    const { alternatePaymentMethodsQueryResult, paymentMethodsQueryResult } = context.apiMachineRef.getSnapshot().context;
    return getPaymentOptionAndMethod(context.form, alternatePaymentMethodsQueryResult.data, paymentMethodsQueryResult.data);
};
export const constructEligibilityQueryParams = (featureFlag) => {
    const paymentCapabilityEligibilityArr = [
        ELIGIBILITY_TYPE.ALLOWED_PAYMENT_METHODS,
        ELIGIBILITY_TYPE.AUTOPAY
    ];
    if (featureFlag.isWaivedInterestEnabled) {
        paymentCapabilityEligibilityArr.push(ELIGIBILITY_TYPE.PAYMENT_OPTION);
    }
    if (featureFlag.isSecuredInstallmentEnabled ||
        featureFlag.isDigitalCollectionsEnabled) {
        paymentCapabilityEligibilityArr.push(ELIGIBILITY_TYPE.ONE_TIME_PAYMENT);
    }
    return paymentCapabilityEligibilityArr;
};
export const getAnalyticsMessageType = (apiMachineRef, paymentType, paymentOption) => {
    const api = apiMachineRef.getSnapshot().context;
    const { featureFlags } = api;
    const { product, enrollments } = api.accountSummaryQueryResult.data;
    const isIRLActive = isIntroRateLoanPromoAvailableForAutopay(api.paymentEnterpriseEligibilityQueryResult.data);
    const messageTypeDCH = getDCHMessageType(featureFlags.isDigitalCollectionsEnabled, api.accountSummaryQueryResult.data);
    if (messageTypeDCH) {
        return messageTypeDCH;
    }
    let productEnrollmentMessageType;
    if (isWaivedInterestEnrolled(product.type, enrollments)) {
        productEnrollmentMessageType = promoTypes.WAIVED_INTEREST;
    }
    else if (isIntroRateLoanEnrolled(product.type, enrollments)) {
        if (isIRLActive) {
            productEnrollmentMessageType = promoTypes.INTRO_RATE_ACTIVE;
        }
        else {
            productEnrollmentMessageType = promoTypes.INTRO_RATE_EXPIRED;
        }
    }
    else {
        return undefined;
    }
    if (paymentType)
        productEnrollmentMessageType = `${productEnrollmentMessageType}_${paymentType}`;
    if (paymentOption)
        productEnrollmentMessageType = `${productEnrollmentMessageType}_${paymentOption}`;
    return productEnrollmentMessageType;
};
export const getAnalyticsProductType = (paymentEnterpriseEligibilityQueryResult, accountSummaryQueryResult) => {
    const subEligibilities = ['E_CHECK', PINLESS_DEBIT_CARD, CREDIT_CARD];
    const isAutopayRequired = subEligibilities.every((subEligibility) => paymentEnterpriseEligibilityQueryResult.ALLOWED_PAYMENT_METHODS.some((eligibility) => eligibility.subEligibilityType === subEligibility));
    const { enrollments, product } = accountSummaryQueryResult;
    if (isRevolvingCreditProduct(product.type))
        return 'revolving_credit';
    if (isPayLaterProduct(product.type)) {
        if (isWaivedInterestEnrolled(product.type, enrollments))
            return 'pay_later_wi';
        if (isAutopayRequired)
            return 'pay_later_required_autopay';
        return 'pay_later';
    }
    if (isSecuredInstallmentLoanProduct(product.type))
        return 'secured_installment';
    if (isSetpayLegacyProduct(product.type))
        return 'setpay';
    return undefined;
};
export const getAnalyticsPaymentOption = (paymentOption) => {
    if (!paymentOption) {
        return undefined;
    }
    const paymentOptionString = (en.paymentAmountOptionText[paymentOption] ||
        paymentOption.split('Option').join());
    return formatCamelCaseToSpaceSeparateWords(paymentOptionString.toLowerCase()).replace(/ /g, '_');
};
export const getAnalyticsPaymentMethodTypeFromType = (paymentMethod) => {
    if (paymentMethod === PINLESS_DEBIT_CARD)
        return AnalyticsPaymentMethods.DEBIT;
    if (paymentMethod === CREDIT_CARD)
        return AnalyticsPaymentMethods.CREDIT;
    if (paymentMethod === CHECKING)
        return AnalyticsPaymentMethods.CHECKING;
    return AnalyticsPaymentMethods.SAVING;
};
export const getAnalyticsPaymentMethodTypeFromMedium = (paymentMethod) => {
    return getAnalyticsPaymentMethodTypeFromType(paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.paymentMedium);
};
export const getAnalyticsAllowedPaymentMethods = (paymentEnterpriseEligibilityQueryResult, accountSummaryQueryResult, paymentFlow, featureFlags) => {
    const { product } = accountSummaryQueryResult;
    const isAutopayFlow = paymentFlow === paymentTypes.AUTOPAY;
    const allowedPaymentMethods = getPaymentMethodsFromPaymentEnterpriseEligibility(product.type, paymentEnterpriseEligibilityQueryResult, featureFlags).filter((method) => isAutopayFlow && isRevolvingCreditProduct(product.type)
        ? method !== PINLESS_DEBIT_CARD
        : method);
    return allowedPaymentMethods
        .map((pm) => getAnalyticsPaymentMethodTypeFromType(pm))
        .join('/');
};
export const getAccountStatus = (accountSummaryQueryResult, productType) => {
    const billing = accountSummaryQueryResult === null || accountSummaryQueryResult === void 0 ? void 0 : accountSummaryQueryResult.billing;
    const isDelinquent = billing === null || billing === void 0 ? void 0 : billing.pastDue;
    const isRevolvingCredit = isRevolvingCreditProduct(productType);
    const isSettlementEnrolled = (billing === null || billing === void 0 ? void 0 : billing.settlementEnrolled) && isRevolvingCredit;
    const isCapEnrolled = (billing === null || billing === void 0 ? void 0 : billing.customerAssistancePlan) && isRevolvingCredit;
    if (isSettlementEnrolled)
        return AnalyticsAccountStatuses.SETTLEMENT_ENROLLED;
    if (isCapEnrolled)
        return AnalyticsAccountStatuses.CAP_ENROLLED;
    if (isDelinquent)
        return AnalyticsAccountStatuses.DELINQUENT;
    return AnalyticsAccountStatuses.CURRENT;
};
